/**
 * View-model mapper for customer-facing booking detail page.
 * Keeps all display logic out of JSX.
 *
 * Uses the canonical getBookingListStatus helper for display state —
 * the same helper used in producer list, producer detail, and customer list.
 */

import { mapBookingToTimeline, type TimelineStep, type BookingTimelineInput } from './timeline';
import { getBookingPaymentSummary, type PaymentSummary, type PaymentSummaryInput } from './paymentSummary';
import { getBookingListStatus, toneToBadgeVariant, type BookingListStatus } from './listStatus';

export interface CustomerBookingDetailInput {
  id: string;
  accommodation_title: string;
  producer_name: string;
  check_in: string;
  check_out: string;
  guests: number;
  guest_name: string | null;
  guest_email: string;
  guest_phone: string | null;
  guest_message: string | null;
  status: string;
  booking_code: string;
  created_at: string;
  cancelled_at: string | null;
  payment_status: string;
  requires_prepayment: boolean;
  prepayment_amount_ore: number | null;
  amount_paid_ore: number;
  paid_at: string | null;
  total_stay_ore: number | null;
  payment_count: number;
}

export interface CustomerBookingDetailVM {
  title: string;
  producerName: string;
  dateRange: string;
  guestCount: number;
  guestName: string | null;
  guestEmail: string;
  guestPhone: string | null;
  guestMessage: string | null;
  bookingCode: string;
  createdAt: string;
  checkIn: string;
  checkOut: string;
  displayStatus: string;
  statusVariant: 'default' | 'destructive' | 'secondary';
  timelineSteps: TimelineStep[];
  paymentSummary: PaymentSummary;
  nextStepText: string;
}

function formatDateRange(checkIn: string, checkOut: string): string {
  const fmt = (d: string) => {
    try {
      const date = new Date(d);
      return date.toLocaleDateString('sv-SE', { day: 'numeric', month: 'long', year: 'numeric' });
    } catch {
      return d;
    }
  };
  return `${fmt(checkIn)} – ${fmt(checkOut)}`;
}

function getNextStepText(status: string, paymentStatus: string): string {
  if (status === 'cancelled') return 'Bokningen har avbokats.';
  if (status === 'completed') return 'Hoppas du hade en fin vistelse!';
  if (status === 'pending') return 'Värden svarar vanligtvis inom 24 timmar.';
  if (status === 'reserved_pending_payment') return 'Betalning inväntas enligt värdens villkor.';
  if (status === 'confirmed' && paymentStatus === 'paid') return 'Din vistelse närmar sig — hör av dig till värden vid frågor.';
  if (status === 'confirmed') return 'Din bokning är bekräftad. Hör av dig till värden vid frågor.';
  return '';
}

export function mapCustomerBookingDetail(input: CustomerBookingDetailInput): CustomerBookingDetailVM {
  // Canonical display state — same helper used everywhere
  const listStatus: BookingListStatus = getBookingListStatus({
    type: 'accommodation',
    status: input.status,
    paymentStatus: input.payment_status,
    amountPaidOre: input.amount_paid_ore,
    totalStayOre: input.total_stay_ore,
    requiresPrepayment: input.requires_prepayment,
    prepaymentAmountOre: input.prepayment_amount_ore,
  });

  const timelineInput: BookingTimelineInput = {
    bookingStatus: input.status,
    paymentStatus: input.payment_status,
    requiresPrepayment: input.requires_prepayment,
    amountPaidOre: input.amount_paid_ore,
    totalStayOre: input.total_stay_ore,
    createdAt: input.created_at,
    checkIn: input.check_in,
    checkOut: input.check_out,
    cancelledAt: input.cancelled_at,
    paidAt: input.paid_at,
  };

  const paymentInput: PaymentSummaryInput = {
    totalPaidOre: input.amount_paid_ore,
    totalStayOre: input.total_stay_ore,
    prepaymentAmountOre: input.prepayment_amount_ore,
    requiresPrepayment: input.requires_prepayment,
    paymentCount: input.payment_count,
  };

  return {
    title: input.accommodation_title,
    producerName: input.producer_name,
    dateRange: formatDateRange(input.check_in, input.check_out),
    guestCount: input.guests,
    guestName: input.guest_name,
    guestEmail: input.guest_email,
    guestPhone: input.guest_phone,
    guestMessage: input.guest_message,
    bookingCode: input.booking_code,
    createdAt: input.created_at,
    checkIn: input.check_in,
    checkOut: input.check_out,
    displayStatus: listStatus.label,
    statusVariant: toneToBadgeVariant(listStatus.tone),
    timelineSteps: mapBookingToTimeline(timelineInput),
    paymentSummary: getBookingPaymentSummary(paymentInput),
    nextStepText: getNextStepText(input.status, input.payment_status),
  };
}
